// GuidesHome.tsx · v12.10.25.3 (#2106 Tushar 10/30: typing shows ONE filtered list at the top - matching guides and articles; an article
// opens in a drawer inside VoiceSpectrum with "Open in the Help library ›"; 📖 Read on a guide lists its articles in the same drawer) · 2026-10-30
// GuidesHome.tsx · v12.10.25.2 (#2106 the left-menu Help opens the Help page INSIDE VoiceSpectrum; Browse all articles opens the library tab)
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccess } from '../../lib/useAccess';
import { GUIDES, type Guide, type GuideKey } from '../../lib/firstRun';
import HelpGuides from './HelpGuides';
import { Article } from './HelpPage';
import { helpTopicsFor, searchHelp, type HelpCanOpen, type HelpEntry, type HelpRail } from './helpContent';
import './help.css';

// which articles a guide's 📖 Read lists
const SETTINGS_GROUPS: Record<'conf' | 'wf' | 'sec', string[]> = {
  conf: ['Organization', 'AI Agent', 'Call Handling', 'Personalization', 'Vocabulary', 'Communication', 'Knowledge Base', 'Reliability', 'Integrations', 'Settings'],
  wf: ['Workflow'],
  sec: ['Security'],
};

export default function GuidesHome() {
  const [query, setQuery] = useState('');
  const [drawer, setDrawer] = useState<{ title: string; list: HelpEntry[]; open?: HelpEntry } | null>(null);
  const navigate = useNavigate();
  const { me, canOpen } = useAccess();
  // the same "what this person may see" rule as the Help library (#2102): any action on an area; while unknown, everything
  const helpCan: HelpCanOpen = useMemo(() => {
    const anySettings = me ? Object.entries(me.access ?? {}).some(([k, v]) => (k.startsWith('s:') || k.startsWith('sec_')) && (v ?? []).length > 0) : true;
    return (k: string) => (k === '*settings' ? anySettings : canOpen(k));
  }, [me, canOpen]);
  const q = query.trim().toLowerCase();
  const results = useMemo(() => searchHelp(query, helpCan), [query, helpCan]);
  const match = (g: Guide) => !q || q.split(/\s+/).every((w) => `${g.name} ${g.desc} ${g.tour.map((t) => t.title + ' ' + t.text).join(' ')}`.toLowerCase().includes(w));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const flat = (rail: HelpRail) => helpTopicsFor(rail, helpCan).flatMap((g) => g.entries);
  function guideArticles(k: GuideKey): HelpEntry[] {
    if (k === 'fam') return flat('start');
    if (k === 'daily') return [...flat('messages'), ...flat('followups'), ...flat('contacts')];
    if (k === 'rep') return searchHelp('reports', helpCan);
    const groups = SETTINGS_GROUPS[k];
    return flat('settings').filter((e) => groups.includes(e.article.group ?? e.section.title));
  }
  function openLibrary(e?: HelpEntry) {
    const first = helpTopicsFor('start', helpCan)[0]?.entries[0];
    const t = e ?? first;
    const url = t ? `/help?s=${encodeURIComponent(t.section.rail)}&a=${encodeURIComponent(t.article.id)}` : '/help';
    window.open(url, '_blank', 'noopener');
  }
  function read(k: GuideKey) {
    const g = GUIDES.find((x) => x.key === k);
    setDrawer({ title: g ? `${g.name} · articles` : 'Articles', list: guideArticles(k) });
  }

  const resultList = q.length > 0 ? (
    <div className="fr-results" data-testid="guides-results">
      <div className="fr-hsec">Articles <span>· {results.length} {results.length === 1 ? 'match' : 'matches'} - click one to read it here</span></div>
      {results.slice(0, 20).map((e) => (
        <button key={e.article.id} type="button" className="fr-result" onClick={() => setDrawer({ title: 'Search', list: results, open: e })}>
          <b>{e.article.title}</b><span>{e.article.menuPath}</span>
        </button>
      ))}
      {results.length === 0 && <p className="fr-muted">No article matches - try other words, or Browse all articles.</p>}
    </div>
  ) : null;

  return (
    <div className="tenant-page guides-home">
      <HelpGuides query={query} onQuery={setQuery} onBrowse={() => openLibrary()} results={resultList} match={match} onRead={read} />
      {drawer && (
        <>
          <div className="gh-scrim" onClick={() => setDrawer(null)} />
          <aside className="gh-drawer" role="dialog" aria-label="Help article">
            <div className="gh-top">
              {drawer.open && drawer.list.length > 1
                ? <button type="button" className="fr-btn" onClick={() => setDrawer({ ...drawer, open: undefined })}>‹ {drawer.title === 'Search' ? 'Results' : 'Articles'}</button>
                : <b>{drawer.open ? 'Help' : drawer.title}</b>}
              <span className="gh-sp" />
              <button type="button" className="fr-link" onClick={() => openLibrary(drawer.open)}>Open in the Help library ›</button>
              <button type="button" className="fr-btn" aria-label="Close" onClick={() => setDrawer(null)}>✕</button>
            </div>
            <div className="gh-body">
              {drawer.open
                ? <Article entry={drawer.open} rail={drawer.open.section.rail} onOpenApp={(to) => { setDrawer(null); navigate(to); }} />
                : drawer.list.length === 0
                  ? <p className="fr-muted">No articles here for your user group.</p>
                  : drawer.list.map((e) => (
                    <button key={e.article.id} type="button" className="fr-result" onClick={() => setDrawer({ ...drawer, open: e })}>
                      <b>{e.article.title}</b><span>{e.article.menuPath}</span>
                    </button>
                  ))}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
